import { useEffect, useState } from "react";

export type WidgetState = {
  lastQuery?: string;
  selectedId?: string;
  showFullText?: boolean;
  sort?: "deadline" | "title";
};

export type OpenAiApi = {
  toolOutput?: unknown;
  toolResponseMetadata?: Record<string, unknown>;
  widgetState?: WidgetState;
  setWidgetState?: (state: WidgetState) => void;
  callTool?: (name: string, args: Record<string, unknown>) => Promise<void>;
  openExternal?: (options: { href: string }) => void;
  notifyIntrinsicHeight?: () => void;
  onToolOutputChanged?: (handler: () => void) => void;
  offToolOutputChanged?: (handler: () => void) => void;
  onWidgetStateChanged?: (handler: () => void) => void;
  offWidgetStateChanged?: (handler: () => void) => void;
};

declare global {
  interface Window {
    openai?: OpenAiApi;
  }
}

function getOpenAi(): OpenAiApi | undefined {
  return window.openai;
}

export function useOpenAiState() {
  const [toolOutput, setToolOutput] = useState<unknown>(() => getOpenAi()?.toolOutput);
  const [toolResponseMetadata, setToolResponseMetadata] = useState<Record<string, unknown> | undefined>(
    () => getOpenAi()?.toolResponseMetadata
  );
  const [widgetState, setWidgetState] = useState<WidgetState>(() => getOpenAi()?.widgetState ?? {});

  useEffect(() => {
    const openai = getOpenAi();
    if (!openai) {
      return;
    }

    const updateToolOutput = () => {
      setToolOutput(openai.toolOutput);
      setToolResponseMetadata(openai.toolResponseMetadata);
    };

    const updateWidgetState = () => {
      setWidgetState(openai.widgetState ?? {});
    };

    if (openai.onToolOutputChanged) {
      openai.onToolOutputChanged(updateToolOutput);
    }

    if (openai.onWidgetStateChanged) {
      openai.onWidgetStateChanged(updateWidgetState);
    }

    const interval = window.setInterval(() => {
      updateToolOutput();
      updateWidgetState();
    }, 600);

    return () => {
      if (openai.offToolOutputChanged) {
        openai.offToolOutputChanged(updateToolOutput);
      }
      if (openai.offWidgetStateChanged) {
        openai.offWidgetStateChanged(updateWidgetState);
      }
      window.clearInterval(interval);
    };
  }, []);

  const api = getOpenAi();

  const updateWidgetState = (next: WidgetState) => {
    setWidgetState(next);
    api?.setWidgetState?.(next);
  };

  return { toolOutput, toolResponseMetadata, widgetState, updateWidgetState, api };
}

export function useNotifyIntrinsicHeight(deps: unknown[] = []) {
  useEffect(() => {
    const openai = getOpenAi();
    if (!openai?.notifyIntrinsicHeight) {
      return;
    }
    openai.notifyIntrinsicHeight();
  }, deps);
}
